import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import {
  MainsiteBannerInterface,
  MainsiteBannerListingInterface,
  MainsiteBlogPostListingInterface,
  MainsiteTncListingInterface
} from "./website-banner.interface";
@Injectable({
  providedIn: "root"
})
export class WebsiteConfigurationService {
  private basePath: string = `${environment.url}/api/MainsiteBanner/`;
  private basePathTnc: string = `${environment.url}/api/MainsiteTnc/`;
  private basePathBlog: string = `${environment.url}/api/MainsiteBlog/`;

  private previewData: PostPreviewData | null = null;
  private previewTnc: PostPreviewTnc | null = null;

  constructor(private http: HttpClient) {}

  setPreviewData(data: PostPreviewData): void {
    this.previewData = data;
    sessionStorage.setItem("preview-post", JSON.stringify(data));
  }

  getPreviewData(): PostPreviewData | null {
    if (this.previewData) return this.previewData;

    const stored = sessionStorage.getItem("preview-post");
    return stored ? JSON.parse(stored) : null;
  }

  setPreviewTnc(data: PostPreviewTnc): void {
    this.previewTnc = data;
    sessionStorage.setItem("preview-tnc", JSON.stringify(data));
  }

  getPreviewTncData(): PostPreviewTnc | null {
    if (this.previewTnc) return this.previewTnc;

    const stored = sessionStorage.getItem("preview-tnc");
    return stored ? JSON.parse(stored) : null;
  }
  clearPreviewData(): void {
    this.previewData = null;
  }

  addBanner(newBanner: FormData): Observable<any> {
    return this.http.post(`${this.basePath}AddBanner`, newBanner);
  }

  getBannerById(id: string): Observable<MainsiteBannerInterface> {
    return this.http.get<MainsiteBannerInterface>(
      `${this.basePath}GetBannerById/${id}`
    );
  }

  deactivateBanner(id: string): Observable<any> {
    return this.http.post(`${this.basePath}DeactivateBanner/${id}`, null);
  }

  updateBanner(id: string, formData: FormData): Observable<any> {
    return this.http.post(`${this.basePath}UpdateBanner/${id}`, formData);
  }

  getBannerListing(value: {
    limit: number;
    offset: number;
    text: string;
    columnIndex: number;
    sortDirection: string;
    statusList: string[];
    startDate?: Date;
    endDate?: Date;
  }) {
    return this.http.post<{
      count: number;
      data: MainsiteBannerListingInterface[];
    }>(`${this.basePath}GetBannerListing`, {
      limit: value.limit,
      offset: value.offset,
      text: value.text,
      columnIndex: value.columnIndex,
      sortDirection: value.sortDirection,
      statusList: value.statusList,
      startDate: value.startDate,
      endDate: value.endDate
    });
  }

  getBlogPostListing(value: {
    limit: number;
    offset: number;
    text: string;
    columnIndex: number;
    sortDirection: string;
    statusList: string[];
    type: string;
    startDate?: Date;
    endDate?: Date;
  }) {
    return this.http.post<{
      count: number;
      data: MainsiteBlogPostListingInterface;
    }>(`${this.basePathBlog}GetBlogPostListing`, {
      limit: value.limit,
      offset: value.offset,
      text: value.text,
      columnIndex: value.columnIndex,
      sortDirection: value.sortDirection,
      statusList: value.statusList,
      type: value.type,
      startDate: value.startDate,
      endDate: value.endDate
    });
  }

  addBlog(newBlog: FormData): Observable<any> {
    return this.http.post(`${this.basePathBlog}AddBlogPost`, newBlog);
  }

  updateBlog(id: string, formData: FormData): Observable<any> {
    return this.http.post(`${this.basePathBlog}UpdateBlogPost/${id}`, formData);
  }

  getBlogPostById(id: string): Observable<any> {
    return this.http.get(`${this.basePathBlog}GetBlogPostById/${id}`);
  }

  getAllCategories(type: string): Observable<any> {
    return this.http.get(`${this.basePathBlog}GetAllCategories?type=${type}`);
  }

  updateBlogStatusToDraft(id: string): Observable<any> {
    return this.http.post(
      `${this.basePathBlog}UpdateBlogStatusToDraft/${id}`,
      null
    );
  }

  getTncListing(value: {
    limit: number;
    offset: number;
    text: string;
    columnIndex: number;
    sortDirection: string;
    statusList: string[];
    startDate?: Date;
    endDate?: Date;
  }) {
    return this.http.post<{
      count: number;
      data: MainsiteTncListingInterface[];
      before6Count: number;
      after6Count: number;
    }>(`${this.basePathTnc}GetTncListing`, {
      limit: value.limit,
      offset: value.offset,
      text: value.text,
      columnIndex: value.columnIndex,
      sortDirection: value.sortDirection,
      statusList: value.statusList,
      startDate: value.startDate,
      endDate: value.endDate
    });
  }

  addTnc(newTnc: INewTNC): Observable<any> {
    return this.http.post(`${this.basePathTnc}AddTnc`, newTnc);
  }

  updateTnc(id: string, newTnc: INewTNC): Observable<any> {
    return this.http.post(`${this.basePathTnc}UpdateTnc?id=${id}`, newTnc);
  }

  getTncById(id: string) {
    return this.http.get(`${this.basePathTnc}GetTncClauseById/${id}`);
  }

  setToTop(id: string) {
    return this.http.post(`${this.basePathTnc}SetTncToTop?id=${id}`, null);
  }

  deleteById(id: string) {
    return this.http.get(`${this.basePathTnc}DeleteById/${id}`);
  }
}

export interface PostPreviewData {
  title: string;
  content: string;
  category: string;
}

export interface PostPreviewTnc {
  tncList: {
    type: string; // TNC title / group
    isTopTnc: boolean;
    tncList: {
      title: string; // clause subtitle
      description: string; // clause content
      sequence: number;
      lastUpdatedAt?: string;
    }[];
  }[];
  lastUpdatedBy?: string; // latest updated date
}

export interface INewTNC {
  title: string;
  status: string;
  clauses: {
    title: string;
    content: string;
  }[];
  deletedTncClause: string[];
}
